import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { getInitialMonth } from '../utils/format';

// ── Manual Entry Modal ───────────────────────────────────────────────────────
function ManualEntryModal({ users, onClose, onSaved }) {
  const [form, setForm] = useState({
    user_id: '',
    session_date: new Date().toISOString().slice(0,10),
    clock_in: '10:00',
    clock_out: '',
    note: '',
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    if (!form.user_id) return toast.error('Select an employee');
    if (!form.clock_in) return toast.error('Clock-in time is required');
    setSaving(true);
    try {
      await api.post('/attendance-sessions/manual', form);
      toast.success('Attendance saved');
      onSaved();
      onClose();
    } catch(err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:420 }}>
        <div className="modal-title">Manual Attendance Entry</div>
        <p style={{ fontSize:12, color:'var(--text2)', marginBottom:16 }}>
          Use this to record attendance when someone forgot to clock in, or to correct a missing clock-out.
        </p>

        <div className="form-row" style={{ marginBottom:12 }}>
          <label className="form-label">Employee</label>
          <select className="form-input" value={form.user_id} onChange={e => upd('user_id', e.target.value)}>
            <option value="">— Select employee —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.username})</option>)}
          </select>
        </div>

        <div className="form-row" style={{ marginBottom:12 }}>
          <label className="form-label">Date</label>
          <input type="date" className="form-input" value={form.session_date}
            max={new Date().toISOString().slice(0,10)}
            onChange={e => upd('session_date', e.target.value)} />
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
          <div className="form-row">
            <label className="form-label">Clock In *</label>
            <input type="time" className="form-input" value={form.clock_in}
              onChange={e => upd('clock_in', e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">Clock Out <span style={{fontWeight:400,color:'var(--text3)'}}>(optional)</span></label>
            <input type="time" className="form-input" value={form.clock_out}
              onChange={e => upd('clock_out', e.target.value)} />
            {!form.clock_out && (
              <div style={{ fontSize:10, color:'#D97706', marginTop:3 }}>
                Leave blank → will auto-close at scheduled end time
              </div>
            )}
          </div>
        </div>

        <div className="form-row" style={{ marginBottom:20 }}>
          <label className="form-label">Reason / Note</label>
          <input type="text" className="form-input" placeholder="e.g. Forgot to clock in"
            value={form.note} onChange={e => upd('note', e.target.value)} />
        </div>

        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Attendance'}
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_META = {
  P:  { label:'P',  bg:'#DCFCE7', color:'#15803D', title:'Present' },
  H:  { label:'H',  bg:'#FEF9C3', color:'#854D0E', title:'Half Day' },
  A:  { label:'A',  bg:'#FEE2E2', color:'#B91C1C', title:'Absent' },
  PL: { label:'PL', bg:'#DBEAFE', color:'#1E40AF', title:'Paid Leave' },
  UL: { label:'UL', bg:'#FFEDD5', color:'#C2410C', title:'Unpaid Leave' },
  WO: { label:'WO', bg:'#F1F5F9', color:'#64748B', title:'Week Off' },
  HO: { label:'HO', bg:'#F5F3FF', color:'#6D28D9', title:'Holiday' },
  '': { label:'—',  bg:'var(--surface2)', color:'var(--text3)', title:'No Data' },
};

function fmtHrs(h) {
  if (!h && h !== 0) return '—';
  return `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m`;
}

function monthLabel(m) {
  return new Date(m + '-01').toLocaleString('default', { month: 'long', year: 'numeric' });
}

// Is this the month still in progress right now (IST, matching business timezone)? Syncing or
// generating payroll for a month that hasn't ended yet is almost never intentional.
function isCurrentMonth(m) {
  return m === new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }).slice(0, 7);
}

// ── Policy Settings Panel ────────────────────────────────────────────────────
function PolicyPanel() {
  const qc = useQueryClient();
  const { data: policy } = useQuery({ queryKey:['att-policy'], queryFn:() => api.get('/attendance-sessions/policy').then(r=>r.data) });
  const [form, setForm] = useState(null);
  const upd = (k,v) => setForm(f => ({...f,[k]:v}));

  const saveMutation = useMutation({
    mutationFn: data => api.patch('/attendance-sessions/policy', data),
    onSuccess: () => { qc.invalidateQueries(['att-policy']); setForm(null); toast.success('Policy saved'); },
    onError: () => toast.error('Failed'),
  });

  const p = form || policy;
  if (!p) return null;

  return (
    <div className="card" style={{ padding:'20px 24px', marginBottom:16 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:16 }}>
        Attendance Policy
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:12 }}>
        <div className="form-row">
          <label className="form-label">Half day threshold (hrs)</label>
          <input className="form-input" type="number" step="0.5" min="1" max="8"
            value={p.half_day_min_hours||4}
            onChange={e => { setForm(form||{...policy}); upd('half_day_min_hours', e.target.value); }} />
          <div style={{ fontSize:10, color:'var(--text3)', marginTop:2 }}>Less than this = half day</div>
        </div>
        <div className="form-row">
          <label className="form-label">Full day threshold (hrs)</label>
          <input className="form-input" type="number" step="0.5" min="1" max="12"
            value={p.full_day_min_hours||7}
            onChange={e => { setForm(form||{...policy}); upd('full_day_min_hours', e.target.value); }} />
          <div style={{ fontSize:10, color:'var(--text3)', marginTop:2 }}>Above this = full present</div>
        </div>
        <div className="form-row">
          <label className="form-label">Late grace (mins)</label>
          <input className="form-input" type="number" min="0" max="60" defaultValue={15} disabled />
          <div style={{ fontSize:10, color:'var(--text3)', marginTop:2 }}>Set in Schedule settings</div>
        </div>
      </div>

      {/* Late deduction */}
      <div style={{ display:'flex', alignItems:'center', gap:16, padding:'12px 16px', background:'var(--surface2)', borderRadius:8, marginBottom:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:600 }}>Late arrival deduction</div>
          <div style={{ fontSize:11, color:'var(--text2)' }}>
            Every {p.lates_per_halfday||3} late arrivals = 1 half-day salary deduction
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <label style={{ fontSize:12 }}>Enable</label>
          <input type="checkbox" checked={!!p.late_deduction_enabled}
            onChange={e => { setForm(form||{...policy}); upd('late_deduction_enabled', e.target.checked); }} />
          {p.late_deduction_enabled && (
            <input className="form-input" type="number" min="1" max="10" style={{ width:60 }}
              value={p.lates_per_halfday||3}
              onChange={e => upd('lates_per_halfday', parseInt(e.target.value))} />
          )}
        </div>
      </div>

      {/* Permitted leave */}
      <div style={{ display:'flex', alignItems:'center', gap:16, padding:'12px 16px', background:'var(--surface2)', borderRadius:8, marginBottom:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:600 }}>Permitted leave per month</div>
          <div style={{ fontSize:11, color:'var(--text2)' }}>
            The first {p.monthly_free_leave_days ?? 0} absent day(s) each month are not deducted from salary in payroll
          </div>
        </div>
        <input className="form-input" type="number" min="0" max="5" step="0.5" style={{ width:70 }}
          value={p.monthly_free_leave_days ?? 0}
          onChange={e => { setForm(form||{...policy}); upd('monthly_free_leave_days', e.target.value); }} />
      </div>

      {/* Overtime */}
      <div style={{ display:'flex', alignItems:'center', gap:16, padding:'12px 16px', background:'var(--surface2)', borderRadius:8, marginBottom:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:600 }}>Overtime tracking</div>
          <div style={{ fontSize:11, color:'var(--text2)' }}>
            Track hours worked beyond {p.overtime_threshold_hours||9}h at {p.overtime_multiplier||1.5}x rate
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <label style={{ fontSize:12 }}>Enable</label>
          <input type="checkbox" checked={!!p.overtime_enabled}
            onChange={e => { setForm(form||{...policy}); upd('overtime_enabled', e.target.checked); }} />
        </div>
      </div>

      {form && (
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button className="btn-ghost" onClick={() => setForm(null)}>Cancel</button>
          <button className="btn-primary" onClick={() => saveMutation.mutate(form)}>Save Policy</button>
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function AttendanceRegisterPage() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(getInitialMonth);
  const [tab, setTab] = useState('register');
  const [syncing, setSyncing] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [closingStale, setClosingStale] = useState(false);
  const [selectedPayrollEmps, setSelectedPayrollEmps] = useState([]);
  const [dayEditCell, setDayEditCell] = useState(null); // { userId, empName, date }
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);

  // Follow the global month selector (top-right on every page), same as Reports/Work Log/etc —
  // this page used to run its own disconnected month, defaulting to "today" and ignoring
  // whatever was already selected globally, which is exactly what caused the accidental
  // September sync/payroll run.
  useEffect(() => { const h = e => setMonth(e.detail); window.addEventListener('monthChange', h); return () => window.removeEventListener('monthChange', h); }, []);
  // The global dropdown only covers the last 8 months — picking an older month here (via the
  // native month input, useful for back-dated payroll corrections) still broadcasts the change
  // so the global selector picks it up whenever it's within its own range.
  function changeMonth(v) { setMonth(v); window.dispatchEvent(new CustomEvent('monthChange', { detail: v })); }

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then(r => r.data),
  });

  const { data: summaryData, isLoading } = useQuery({
    queryKey: ['att-monthly-summary', month],
    queryFn: () => api.get(`/attendance-sessions/monthly-summary?month=${month}`).then(r => r.data),
  });

  const { data: lockData } = useQuery({
    queryKey: ['payroll-lock', month],
    queryFn: () => api.get(`/hr/payroll/lock-status?month=${month}`).then(r => r.data),
  });

  const { data: previewData } = useQuery({
    queryKey: ['payroll-preview', month],
    queryFn: () => api.get(`/hr/payroll/preview?month=${month}`).then(r => r.data),
    enabled: tab === 'payroll',
  });

  const lockMutation = useMutation({
    mutationFn: ({ lock }) => lock
      ? api.post('/hr/payroll/lock', { month, note:'Locked by owner' })
      : api.post('/hr/payroll/unlock', { month }),
    onSuccess: () => { qc.invalidateQueries(['payroll-lock', month]); toast.success(lockData?.locked ? 'Month unlocked' : 'Month locked'); },
  });

  const generateMutation = useMutation({
    mutationFn: employee_ids => api.post('/hr/payroll/generate', { month, ...(employee_ids?.length ? { employee_ids } : {}) }),
    onSuccess: r => { toast.success(`Payroll generated for ${r.data.count} employee(s)`); qc.invalidateQueries(['payroll-preview', month]); setSelectedPayrollEmps([]); },
    onError: () => toast.error('Failed to generate payroll'),
  });

  const dayStatusMutation = useMutation({
    mutationFn: ({ userId, date, status }) => api.post('/attendance-sessions/day-status', { user_id: userId, date, status }),
    onSuccess: () => { toast.success('Attendance corrected'); qc.invalidateQueries(['att-monthly-summary']); qc.invalidateQueries(['payroll-preview']); qc.invalidateQueries(['hr-leaves']); qc.invalidateQueries(['hr-leave-balance']); setDayEditCell(null); },
    onError: () => toast.error('Failed to correct attendance'),
  });

  async function handleAutoClose() {
    setClosingStale(true);
    try {
      const r = await api.post('/attendance-sessions/auto-close-stale');
      if (r.data.closed === 0) toast.success('No open sessions to close');
      else toast.success(`Auto-closed ${r.data.closed} session(s): ${r.data.sessions.join(', ')}`);
      qc.invalidateQueries(['att-monthly-summary', month]);
    } catch { toast.error('Auto-close failed'); }
    finally { setClosingStale(false); }
  }

  async function handleSync() {
    setSyncing(true);
    try {
      const r = await api.post('/attendance-sessions/sync-month', { month });
      toast.success(`Sync complete — ${r.data.synced} sessions synced, ${r.data.markedAbsent} marked absent`);
      qc.invalidateQueries(['att-monthly-summary', month]);
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  }

  const summaries = summaryData?.summaries || [];
  const workingDayList = summaryData?.working_day_list || [];

  // Day labels for register header
  const dayNumbers = workingDayList.map(d => ({
    ds: d,
    day: new Date(d).getDate(),
    dow: new Date(d).toLocaleDateString('en-IN',{weekday:'short'}),
    isSat: new Date(d).getDay() === 6,
  }));

  return (
    <div>
      <div className="section-head" style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <h2>Attendance Register — {monthLabel(month)}</h2>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <input type="month" className="form-input" value={month} onChange={e => changeMonth(e.target.value)} style={{ width:160 }} />
          <button className="btn-ghost" onClick={handleAutoClose} disabled={closingStale} style={{ fontSize:12 }}
            title="Auto-close any sessions where someone forgot to clock out">
            {closingStale ? 'Closing…' : '⏱ Fix Missed Clock-Outs'}
          </button>
          <button className="btn-primary" onClick={() => setShowManual(true)} style={{ fontSize:12 }}>
            + Manual Entry
          </button>
          <button className="btn-ghost" onClick={() => setShowSyncConfirm(true)} disabled={syncing} style={{ fontSize:12 }}>
            {syncing ? 'Syncing…' : '⟳ Sync Month'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:16 }}>
        {[['register','Attendance Register'],['summary','Employee Summary'],['payroll','Payroll Preview'],['policy','Policy Settings']].map(([k,label]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding:'6px 14px', borderRadius:6, border:'1px solid var(--border)', fontSize:12,
              fontWeight:tab===k?700:400, background:tab===k?'var(--accent)':'var(--surface)',
              color:tab===k?'#fff':'var(--text2)', cursor:'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── REGISTER TAB — the HR grid ─────────────────────────────────── */}
      {tab === 'register' && (
        <div>
          <div style={{ marginBottom:12, display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
            {Object.entries(STATUS_META).filter(([k]) => k).map(([k,v]) => (
              <span key={k} style={{ fontSize:11, padding:'2px 8px', borderRadius:6, background:v.bg, color:v.color, fontWeight:600 }}>
                {v.label} = {v.title}
              </span>
            ))}
          </div>

          {isLoading && <div className="loading-screen"><div className="spinner" /></div>}

          {!isLoading && summaries.length > 0 && (
            <div style={{ overflowX:'auto' }}>
              <table style={{ borderCollapse:'collapse', fontSize:11, minWidth:'100%' }}>
                <thead>
                  <tr style={{ background:'var(--surface2)' }}>
                    <th style={{ padding:'8px 12px', textAlign:'left', whiteSpace:'nowrap', minWidth:160, position:'sticky', left:0, background:'var(--surface2)', zIndex:2, borderRight:'2px solid var(--border)' }}>
                      Employee
                    </th>
                    {dayNumbers.map(d => (
                      <th key={d.ds} style={{ padding:'4px 6px', textAlign:'center', minWidth:36, color: d.isSat ? 'var(--accent)' : 'var(--text2)' }}>
                        <div>{d.day}</div>
                        <div style={{ fontSize:9, opacity:0.7 }}>{d.dow}</div>
                      </th>
                    ))}
                    <th style={{ padding:'8px 10px', textAlign:'center', minWidth:44, color:'#15803D' }}>P</th>
                    <th style={{ padding:'8px 10px', textAlign:'center', minWidth:44, color:'#854D0E' }}>H</th>
                    <th style={{ padding:'8px 10px', textAlign:'center', minWidth:44, color:'#B91C1C' }}>A</th>
                    <th style={{ padding:'8px 10px', textAlign:'center', minWidth:44, color:'#D97706' }}>Late</th>
                    <th style={{ padding:'8px 10px', textAlign:'center', minWidth:60 }}>Hrs</th>
                    <th style={{ padding:'8px 10px', textAlign:'center', minWidth:60, color:'var(--accent)' }}>OT Hrs</th>
                  </tr>
                </thead>
                <tbody>
                  {summaries.map((emp, i) => (
                    <tr key={emp.emp_id} style={{ background: i%2===0 ? 'var(--surface)' : 'var(--surface2)', borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'6px 12px', fontWeight:600, whiteSpace:'nowrap', position:'sticky', left:0, background: i%2===0 ? 'var(--surface)' : 'var(--surface2)', zIndex:1, borderRight:'2px solid var(--border)' }}>
                        <div>{emp.full_name}</div>
                        <div style={{ fontSize:10, color:'var(--text3)', fontWeight:400 }}>{emp.designation || emp.department || emp.employee_code}</div>
                      </td>
                      {dayNumbers.map(d => {
                        const code = emp.day_details[d.ds] || '';
                        const meta = STATUS_META[code] || STATUS_META[''];
                        const editable = emp.user_id && ['', 'P', 'H', 'A'].includes(code);
                        return (
                          <td key={d.ds} style={{ padding:'4px', textAlign:'center' }}>
                            <div
                              onClick={() => editable && setDayEditCell({ userId: emp.user_id, empName: emp.full_name, date: d.ds })}
                              style={{ width:28, height:24, borderRadius:4, display:'flex', alignItems:'center', justifyContent:'center',
                                background:meta.bg, color:meta.color, fontWeight:700, fontSize:10, margin:'auto',
                                cursor: editable ? 'pointer' : 'default' }}
                              title={`${emp.full_name} · ${d.ds} · ${meta.title}${editable ? ' · Click to correct' : ''}`}>
                              {meta.label}
                            </div>
                          </td>
                        );
                      })}
                      <td style={{ textAlign:'center', fontWeight:700, color:'#15803D' }}>{emp.present_days}</td>
                      <td style={{ textAlign:'center', fontWeight:700, color:'#854D0E' }}>{emp.half_days}</td>
                      <td style={{ textAlign:'center', fontWeight:700, color:'#B91C1C' }}>{emp.absent_days}</td>
                      <td style={{ textAlign:'center', color: emp.late_days > 0 ? '#D97706' : 'var(--text3)' }}>{emp.late_days}</td>
                      <td style={{ textAlign:'center', color:'var(--text2)' }}>{fmtHrs(emp.total_hours)}</td>
                      <td style={{ textAlign:'center', color: emp.overtime_hours > 0 ? 'var(--accent)' : 'var(--text3)' }}>
                        {emp.overtime_hours > 0 ? fmtHrs(emp.overtime_hours) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!isLoading && summaries.length === 0 && (
            <div className="card empty" style={{ padding:40, textAlign:'center' }}>
              <div style={{ fontSize:32, marginBottom:8 }}>📋</div>
              <div style={{ fontWeight:600 }}>No employee data yet</div>
              <div style={{ fontSize:13, color:'var(--text2)', marginTop:4 }}>Add employees in HR → Employees first</div>
            </div>
          )}
        </div>
      )}

      {/* ── SUMMARY TAB ──────────────────────────────────────────────── */}
      {tab === 'summary' && (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {summaries.map(emp => (
            <div key={emp.emp_id} className="card" style={{ padding:'16px 20px' }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:14 }}>{emp.full_name}</div>
                  <div style={{ fontSize:12, color:'var(--text2)' }}>{emp.designation} · {emp.department} · {emp.employee_code}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:11, color:'var(--text3)' }}>Effective working days</div>
                  <div style={{ fontSize:18, fontWeight:800, color:'var(--accent)' }}>{emp.effective_present} / {emp.working_days}</div>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:10 }}>
                {[
                  ['Present', emp.present_days, '#15803D'],
                  ['Half Days', emp.half_days, '#854D0E'],
                  ['Absent', emp.absent_days, '#B91C1C'],
                  ['Late', emp.late_days, '#D97706'],
                  ['Paid Leave', emp.paid_leave, '#1E40AF'],
                  ['Unpaid Leave', emp.unpaid_leave, '#C2410C'],
                ].map(([label, val, color]) => (
                  <div key={label} style={{ textAlign:'center', padding:'8px 4px', background:'var(--surface2)', borderRadius:6 }}>
                    <div style={{ fontSize:18, fontWeight:800, color }}>{val}</div>
                    <div style={{ fontSize:10, color:'var(--text3)' }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:20, marginTop:10, fontSize:12, color:'var(--text2)' }}>
                <span>Total hours: <strong>{fmtHrs(emp.total_hours)}</strong></span>
                {emp.overtime_hours > 0 && <span style={{ color:'var(--accent)' }}>Overtime: <strong>{fmtHrs(emp.overtime_hours)}</strong></span>}
                {emp.late_deduction_halfdays > 0 && <span style={{ color:'#D97706' }}>Late deduction: <strong>-{emp.late_deduction_halfdays} half day(s)</strong></span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── PAYROLL PREVIEW TAB ───────────────────────────────────────── */}
      {tab === 'payroll' && (
        <div>
          {/* Lock status + actions */}
          <div className="card" style={{ padding:'14px 20px', marginBottom:16, display:'flex', alignItems:'center', gap:16, background: lockData?.locked ? '#FEF2F2' : 'var(--surface)' }}>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600, fontSize:13, color: lockData?.locked ? '#B91C1C' : 'var(--text1)' }}>
                {lockData?.locked ? `🔒 ${month} is locked` : `🔓 ${month} is open for editing`}
              </div>
              <div style={{ fontSize:11, color:'var(--text2)' }}>
                {lockData?.locked
                  ? `Locked on ${new Date(lockData.lockedAt).toLocaleDateString('en-IN')} — payroll cannot be regenerated`
                  : 'Sync attendance first, then generate payroll. Lock the month when done.'}
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              {!lockData?.locked && (
                <button className="btn-primary" onClick={() => setShowGenerateConfirm(true)} disabled={generateMutation.isPending}>
                  {generateMutation.isPending ? 'Generating…' : selectedPayrollEmps.length ? `⚡ Generate Payroll (${selectedPayrollEmps.length})` : '⚡ Generate Payroll (All)'}
                </button>
              )}
              <button className="btn-ghost" style={{ color: lockData?.locked ? '#15803D' : '#B91C1C' }}
                onClick={() => lockMutation.mutate({ lock: !lockData?.locked })}>
                {lockData?.locked ? '🔓 Unlock' : '🔒 Lock Month'}
              </button>
            </div>
          </div>

          {previewData?.preview && (
            <div style={{ overflowX:'auto' }}>
              <table style={{ borderCollapse:'collapse', fontSize:12, width:'100%' }}>
                <thead>
                  <tr style={{ background:'var(--surface2)' }}>
                    <th style={{ padding:'8px 14px', width:1 }}>
                      <input type="checkbox"
                        checked={selectedPayrollEmps.length>0 && selectedPayrollEmps.length===previewData.preview.length}
                        onChange={e => setSelectedPayrollEmps(e.target.checked ? previewData.preview.map(r=>r.id) : [])}
                        title="Select all" />
                    </th>
                    {['Employee','Dept','Working Days','Present','Absent','Late','Gross Salary','Est. Net Pay'].map(h => (
                      <th key={h} style={{ padding:'8px 14px', textAlign:'left', fontSize:11, color:'var(--text3)', fontWeight:600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.preview.map((row, i) => (
                    <tr key={row.id} style={{ borderBottom:'1px solid var(--border)', background: i%2===0?'var(--surface)':'var(--surface2)' }}>
                      <td style={{ padding:'8px 14px' }}>
                        <input type="checkbox" checked={selectedPayrollEmps.includes(row.id)}
                          onChange={e => setSelectedPayrollEmps(prev => e.target.checked ? [...prev, row.id] : prev.filter(id => id !== row.id))} />
                      </td>
                      <td style={{ padding:'8px 14px', fontWeight:600 }}>
                        <div>{row.full_name}</div>
                        <div style={{ fontSize:10, color:'var(--text3)' }}>{row.employee_code}</div>
                      </td>
                      <td style={{ padding:'8px 14px', color:'var(--text2)' }}>{row.department}</td>
                      <td style={{ padding:'8px 14px', textAlign:'center' }}>{row.working_days}</td>
                      <td style={{ padding:'8px 14px', textAlign:'center', color:'#15803D', fontWeight:600 }}>{row.present_days}</td>
                      <td style={{ padding:'8px 14px', textAlign:'center', color: row.absent_days>0 ? '#B91C1C' : 'var(--text3)', fontWeight: row.absent_days>0?600:400 }} title={row.free_leave_days>0 ? `${row.free_leave_days} day(s) covered by permitted monthly leave` : ''}>
                        {row.absent_days}{row.free_leave_days>0 && <span style={{fontSize:10, color:'#1D4ED8'}}> ({row.free_leave_days} permitted)</span>}
                      </td>
                      <td style={{ padding:'8px 14px', textAlign:'center', color: row.late_deduction_halfdays>0 ? '#D97706' : 'var(--text3)', fontWeight: row.late_deduction_halfdays>0?600:400 }} title={row.late_deduction_halfdays>0 ? `${row.late_deduction_halfdays} half-day deduction from lateness` : ''}>
                        {row.late_days}{row.late_deduction_halfdays>0 && <span style={{fontSize:10}}> (-{row.late_deduction_halfdays}d)</span>}
                      </td>
                      <td style={{ padding:'8px 14px' }}>₹{parseFloat(row.gross_salary).toLocaleString('en-IN')}</td>
                      <td style={{ padding:'8px 14px', fontWeight:700, color:'var(--accent)' }}>₹{row.estimated_net.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── POLICY TAB ───────────────────────────────────────────────── */}
      {tab === 'policy' && <PolicyPanel />}

      {showSyncConfirm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:420 }}>
            <div className="modal-title">Sync Month</div>
            <div style={{ background:'#FEF3C7', border:'1px solid #FDE68A', borderRadius:8, padding:'14px 16px', marginBottom:16, textAlign:'center' }}>
              <div style={{ fontSize:11, color:'#92400E', fontWeight:600, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:4 }}>You are syncing attendance for</div>
              <div style={{ fontSize:20, fontWeight:800, color:'#92400E' }}>{monthLabel(month)}</div>
            </div>
            {isCurrentMonth(month) && (
              <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#B91C1C', fontWeight:600 }}>
                ⚠ {monthLabel(month)} is still in progress — it hasn't ended yet. Did you mean to sync last month instead?
              </div>
            )}
            <p style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>
              This pulls clock-in sessions into the attendance register for every completed day this month, and marks any day with no session and no approved leave as absent. Double-check the month above.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={() => setShowSyncConfirm(false)}>Cancel</button>
              <button className="btn-primary" disabled={syncing}
                onClick={() => { setShowSyncConfirm(false); handleSync(); }}>
                {syncing ? 'Syncing…' : `Yes, sync ${monthLabel(month)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showGenerateConfirm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:420 }}>
            <div className="modal-title">Generate Payroll</div>
            <div style={{ background:'#FEF3C7', border:'1px solid #FDE68A', borderRadius:8, padding:'14px 16px', marginBottom:16, textAlign:'center' }}>
              <div style={{ fontSize:11, color:'#92400E', fontWeight:600, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:4 }}>You are generating payroll for</div>
              <div style={{ fontSize:20, fontWeight:800, color:'#92400E' }}>{monthLabel(month)}</div>
            </div>
            {isCurrentMonth(month) && (
              <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#B91C1C', fontWeight:600 }}>
                ⚠ {monthLabel(month)} is still in progress — it hasn't ended yet. Payroll is normally generated for a completed month. Did you mean last month instead?
              </div>
            )}
            <p style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>
              {selectedPayrollEmps.length
                ? `This will process ${selectedPayrollEmps.length} selected employee(s).`
                : 'This will process all active employees.'} Double-check the month above — the date picker at the top of the page controls it.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={() => setShowGenerateConfirm(false)}>Cancel</button>
              <button className="btn-primary" disabled={generateMutation.isPending}
                onClick={() => { generateMutation.mutate(selectedPayrollEmps); setShowGenerateConfirm(false); }}>
                {generateMutation.isPending ? 'Generating…' : `Yes, generate for ${monthLabel(month)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showManual && (
        <ManualEntryModal
          users={usersData?.filter(u => u.role !== 'owner') || []}
          onClose={() => setShowManual(false)}
          onSaved={() => qc.invalidateQueries(['att-monthly-summary', month])}
        />
      )}

      {dayEditCell && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:360 }}>
            <div className="modal-title">Correct Attendance</div>
            <p style={{ fontSize:13, color:'var(--text2)', marginBottom:18 }}>
              {dayEditCell.empName} · {new Date(dayEditCell.date+'T00:00:00').toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
              <button className="btn-primary" disabled={dayStatusMutation.isPending}
                onClick={() => dayStatusMutation.mutate({ userId: dayEditCell.userId, date: dayEditCell.date, status: 'present' })}>
                ✓ Mark Present
              </button>
              <button className="btn-sm" style={{ background:'#FEF9C3', color:'#854D0E', fontWeight:600 }} disabled={dayStatusMutation.isPending}
                onClick={() => dayStatusMutation.mutate({ userId: dayEditCell.userId, date: dayEditCell.date, status: 'half_day' })}>
                ◐ Mark Half Day
              </button>
              <button className="btn-sm" style={{ background:'#FEE2E2', color:'#B91C1C', fontWeight:600 }} disabled={dayStatusMutation.isPending}
                onClick={() => {
                  if (window.confirm(`Mark ${dayEditCell.empName} absent on this day? This removes any clock-in/out record for that day — it can't be undone by clicking again.`)) {
                    dayStatusMutation.mutate({ userId: dayEditCell.userId, date: dayEditCell.date, status: 'absent' });
                  }
                }}>
                ✕ Mark Absent
              </button>
              <button className="btn-sm" style={{ background:'#DBEAFE', color:'#1D4ED8', fontWeight:600 }} disabled={dayStatusMutation.isPending}
                onClick={() => {
                  if (window.confirm(`Mark ${dayEditCell.empName} on paid leave for this day? This approves a paid leave request for the day so payroll doesn't deduct it.`)) {
                    dayStatusMutation.mutate({ userId: dayEditCell.userId, date: dayEditCell.date, status: 'paid_leave' });
                  }
                }}>
                🏖 Mark Paid Leave
              </button>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setDayEditCell(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
